import { Map, List, Set, fromJS } from 'immutable'
import { createSelector } from 'reselect'
import { identity, compose, isEmpty, find as ramdaFind } from 'ramda'
import strings from 'utils/strings'
import { createToJsSelector, createDeepEqualSelector } from 'selectors/utils'
import { getTruckIsPreProduction } from 'selectors/truck'
import { RANGE, BOOLEAN, STATE } from 'utils/constants'
import { ORDERED_DOMAIN_IDS } from 'constants/powerTakeOffConstants'
import { capitalizeFirstOnly } from 'utils/utils'
import { safeMin, safeMax } from 'utils/math'
import { isNumber } from 'utils/types'
import {
  pickToArray,
  pick,
  getInOr,
  map,
  getEq,
  push,
  update,
  set,
  get,
  getIn,
  find,
  getInSatisfies,
  safeToJS,
  valueSeq,
} from 'utils/immutable'

const domainsSelector = getInOr(Map(), ['pto', 'domains'])
const dirtySelector = (state) => state.getIn(['validation', 'dirty'])

const domainFragmentsListSelector = (state, props) =>
  state.getIn(['pto', 'domains', props.id, 'fragments'])

const domainNameSelector = (state, props) => {
  state.getIn(['pto', 'domains', props.id, 'name'])
}

const fragmentsSelector = (state) => state.getIn(['pto', 'fragments'])

const fragmentSelector = (state, props) =>
  getInOr(Map(), ['pto', 'fragments', props.id], state)

const diagnosticVariantSelector = (state) =>
  state.getIn(['pto', 'diagnosticVariant'])

const pendingFragmentSelector = (state, props) =>
  state.getIn(['pto', 'fragments', props.id, 'pending'])

const selectedTemplateByIdSelector = (state, props) =>
  getInOr(Map(), ['pto', 'selectedTemplate', props.templateId], state)

export const getTemplateById = createToJsSelector(
  selectedTemplateByIdSelector,
  identity
)

export const isPtoTemplateLoaded = createSelector(
  selectedTemplateByIdSelector,
  (template) => !template.isEmpty()
)

const templatesSelector = getInOr(Map(), ['pto', 'templates'])

const fragmentsByTemplateIdSelector = (state, props) =>
  state.getIn(['pto', 'selectedTemplate', props.templateId, 'fragments'])

const domainsByTemplateIdSelector = (state, props) =>
  state.getIn(['pto', 'selectedTemplate', props.templateId, 'domains'])

const domainByTemplateIdSelector = createSelector(
  domainsByTemplateIdSelector,
  (_, props) => props.domainId,
  (domains, domainId) => domains.get(domainId)
)

export const getFragmentsByTemplateId = createToJsSelector(
  fragmentsByTemplateIdSelector
)

export const getDomainByTemplate = createToJsSelector(
  domainByTemplateIdSelector
)

const getTemplates = createSelector(templatesSelector, identity)
const getTemplatesToJS = createToJsSelector(getTemplates)
export const makeGetTemplatesSelector = () => getTemplatesToJS

// required for card view
const selectTemplateMetaList = createSelector(
  templatesSelector,
  (templates) => {
    if (isEmpty(templates)) return List()
    return compose(
      map(
        pick([
          'minCompatibleVariant',
          'templateId',
          'templateDescription',
          'compatibleVariants',
          'orgId',
          'fragments',
          'name',
          'partNumber',
          'timeStamp',
          'author',
          'transmissionFamily',
          'engineFamily',
        ])
      ),
      valueSeq
    )(templates)
  }
)
export const getTemplateMetaList = createToJsSelector(selectTemplateMetaList)

const booleanValueMapping = Object.freeze({
  [true]: strings.on,
  [false]: strings.off,
})

const getDisplayValueByType = (value, units, type) => {
  if (type === RANGE && units) return `${value} ${units.toUpperCase()}`
  if (type === BOOLEAN) return booleanValueMapping[value]
  return `${capitalizeFirstOnly(value)}`
}

export const selectPtoTemplateDiff = createSelector(
  fragmentsSelector,
  selectedTemplateByIdSelector,
  (fragments, template) => {
    if (template.isEmpty()) return List()
    const selectedTemplateFragments = get('fragments', template)
    const selectedTemplateDomains = get('domains', template)
    const fragmentValueDiffList = selectedTemplateFragments
      .valueSeq()
      .reduce((acc, templateFragment) => {
        const templateFragmentRef = get('refNo', templateFragment)
        const truckFragment = get(templateFragmentRef, fragments)

        const valPath = ['configuration', 'value']
        const unitsPath = ['configuration', 'units']
        const typePath = ['configuration', 'type']

        const fragmentDomainId = get('domain', templateFragment)
        const domainId = getIn(
          [fragmentDomainId, 'id'],
          selectedTemplateDomains
        )
        const domainName = getIn(
          [fragmentDomainId, 'name'],
          selectedTemplateDomains
        )
        const fragmentName = get('name', templateFragment)
        const tempVal = getIn(valPath)(templateFragment)
        const vehicleVal = getIn(valPath)(truckFragment)
        const tempUnits = getInOr(null, unitsPath)(templateFragment)
        const vehicleUnits = getInOr(null, unitsPath)(truckFragment)
        const tempType = getIn(typePath)(templateFragment)
        const vehType = getIn(typePath)(truckFragment)
        const fragmentId = get('id', templateFragment)

        if (tempVal !== vehicleVal) {
          return acc.push(
            Map({
              refNo: templateFragmentRef,
              fragmentId,
              domainId,
              domainName,
              name: fragmentName,
              templateValue: getDisplayValueByType(
                tempVal,
                tempUnits,
                tempType
              ),
              vehicleValue: getDisplayValueByType(
                vehicleVal,
                vehicleUnits,
                vehType
              ),
            })
          )
        }
        return acc
      }, List())

    return fragmentValueDiffList
  }
)

export const getPtoTemplateDiff = createToJsSelector(
  selectPtoTemplateDiff,
  identity
)

const composeVehicleTemplateFragments = (domains, fragments) => {
  const fragmentValueDiffList = fragments
    .valueSeq()
    .reduce((acc, templateFragment) => {
      const templateFragmentRef = get('refNo', templateFragment)
      const truckFragment = get(templateFragmentRef, fragments)

      const valPath = ['configuration', 'value']
      const unitsPath = ['configuration', 'units']
      const typePath = ['configuration', 'type']
      const definedValuesPath = ['configuration', 'definedValues']

      const fragmentDomainId = get('domain', templateFragment)
      const domainName = getIn([fragmentDomainId, 'name'], domains)
      const fragmentName = get('name', templateFragment)
      const vehicleVal = getIn(valPath)(truckFragment)
      const vehicleUnits = getInOr(null, unitsPath)(truckFragment)
      const vehType = getIn(typePath)(truckFragment)
      const rangeValues = getIn(definedValuesPath)(truckFragment)
      const isFragmentOutOfRange = get('isFragmentOutOfRange', templateFragment)
      const fragmentId = get('id', templateFragment)

      return acc.push(
        Map({
          refNo: templateFragmentRef,
          fragmentId,
          fragmentDomainId,
          domainId: domainName,
          name: fragmentName,
          templateValue: getDisplayValueByType(
            vehicleVal,
            vehicleUnits,
            vehType
          ),
          vehicleValue: getDisplayValueByType(
            vehicleVal,
            vehicleUnits,
            vehType
          ),
          valueRaw: vehicleVal,
          vehType,
          vehicleUnits,
          rangeValues,
          isFragmentOutOfRange,
        })
      )
    }, List())

  return safeToJS(fragmentValueDiffList)
}

export const selectPtoVehicleFragments = createSelector(
  domainsSelector,
  fragmentsSelector,
  composeVehicleTemplateFragments
)

export const selectPtoTemplateFragments = createSelector(
  domainsSelector,
  fragmentsByTemplateIdSelector,
  composeVehicleTemplateFragments
)

export const selectTemplateFragmentsFromDiff = createSelector(
  selectPtoTemplateDiff,
  fragmentsByTemplateIdSelector,
  (_, props) => props.templateId,
  (fragmentValueDiffList, fragmentsByTemplate, templateId) => {
    const diffList = fromJS(fragmentValueDiffList)
    const refNoArray = compose(map(getIn(['refNo'])))(diffList)
    const fragmentList = refNoArray.reduce((acc, refNo) => {
      const fragment = getIn([refNo], fragmentsByTemplate)
      if (isEmpty(fragment)) return acc
      return acc.push(fragment.delete('templateId'))
    }, List())
    return safeToJS(fragmentList)
  }
)

const domainsListSelector = createSelector((state) => {
  const domains = getInOr(Map(), ['pto', 'domains'], state)
  return domains.map((item) => {
    const id = item.get('id')
    const fragments = domainFragmentsListSelector(state, { id })

    const domainHasOutofRangeSettings = !!ramdaFind((frag) =>
      fragmentSelector(state, { id: frag }).get('isFragmentOutOfRange')
    )(fragments)

    const newitem = set(
      'domainHasOutofRangeSettings',
      domainHasOutofRangeSettings,
      item
    )
    return newitem
  })
}, compose(map(pick(['name', 'id', 'domainHasOutofRangeSettings'])), pickToArray(ORDERED_DOMAIN_IDS)))

export const getDomainsList = createDeepEqualSelector(
  domainsListSelector,
  safeToJS
)

export const makeGetDomainName = () =>
  createSelector(domainNameSelector, identity)

const domainFragmentsSelector = createSelector(
  domainFragmentsListSelector,
  fragmentsSelector,
  pickToArray
)

export const getDomainFragments = createToJsSelector(domainFragmentsSelector)

const fragmentByIdSelector = createSelector(fragmentSelector, identity)

export const getFragment = createToJsSelector(fragmentByIdSelector)
export const makeGetFragment = () => getFragment

export const getAllCompatibleFragmentsForPowertrain = createSelector(
  fragmentsSelector,
  (fragments) => {
    if (fragments.isEmpty()) return List()
    const compatibleFragments = fragments
      .valueSeq()
      .filter((fragment) => fragment?.get('isFragmentCompatible'))
    return safeToJS(compatibleFragments)
  }
)

export const getAllPendingFragmentsForPTO = createSelector(
  fragmentsSelector,
  (fragments) => {
    if (fragments.isEmpty()) return List()
    const compatibleFragments = fragments
      .valueSeq()
      .filter((fragment) => fragment?.get('pending'))
    return safeToJS(compatibleFragments)
  }
)

const getDisplayValue = (value, definedValues) =>
  !value && definedValues && definedValues.isEmpty() ? strings.n_a : value
export const makeGetFragmentDescription = () =>
  createSelector(
    fragmentByIdSelector,
    (_, props) => props,
    (fragment, { operator, isGroup, isTitle }) => {
      if (fragment.isEmpty()) return null
      // if enables array present - multiple value settings
      const useGroupName =
        isGroup &&
        isTitle &&
        fragment?.getIn(['configuration', 'enables']) &&
        fragment?.getIn(['configuration', 'enables']).size > 1
      const name = useGroupName
        ? get('groupName', fragment)
        : get('name', fragment)

      const configuration = fragment?.get('configuration')
      const value = configuration.get('value')
      const definedValues = configuration.get('definedValues')
      const type = configuration.get('type')
      const units = configuration.get('units')

      // display value and units for non bool type settings
      if (type === BOOLEAN) return name

      const displayValue = getDisplayValue(value, definedValues)

      if (!displayValue) return name
      if (type === STATE) return `${name} ${displayValue}`
      return `${name} ${displayValue} ${units}`
    }
  )

export const makeGetIsPendingFragment = () =>
  createSelector(pendingFragmentSelector, identity)

export const getDiagnosticVariant = createSelector(
  diagnosticVariantSelector,
  identity
)

const getCurrentVehicleSerialFromLocation = () => {
  const { pathname = '' } = window.location
  const [, serialNumber] =
    `${pathname}`.match(/^\/chec\/vehicles\/([A-Z0-9]{0,6})\//) || []
  return serialNumber
}

export const getCurrentVehicleIsPreProduction = (state) => {
  const serialNumber = getCurrentVehicleSerialFromLocation()
  if (serialNumber) {
    return getTruckIsPreProduction(state, { serialNumber })
  }
  return null
}

const DISALLOWED_APP_VARIANTS = []
export const getIsPtoEditBlocked = createSelector(
  getCurrentVehicleIsPreProduction,
  getDiagnosticVariant,
  (isPreProduction, variant) =>
    DISALLOWED_APP_VARIANTS.includes(variant) || isPreProduction
)

const dirtyFragmentsSelector = createSelector(
  dirtySelector,
  fragmentsSelector,
  pickToArray
)

export const getDirtyFragments = createToJsSelector(dirtyFragmentsSelector)

const fragmentDomainIdSelector = (state, props) =>
  state.getIn(['pto', 'fragments', props.id, 'domain'])

export const getFragmentDomainName = createSelector(
  fragmentDomainIdSelector,
  domainsSelector,
  (domainId, domains) => domains.getIn([domainId, 'name'])
)

export const makeGetFragmentDomainName = () => getFragmentDomainName

const pendingDomainsSelector = createSelector(domainsSelector, (domains) =>
  domains.reduce((acc, current) => {
    if (getEq('pendingCount', 0, current)) return acc

    return acc.push(current)
  }, List())
)

export const getPendingDomains = createToJsSelector(pendingDomainsSelector)

export const getHasPendingDomains = createSelector(
  pendingDomainsSelector,
  (domains) => !domains.isEmpty()
)

const groupFragmentsSelector = createSelector(
  fragmentsSelector,
  (_, props) => props.groupId,
  (fragments, groupId) => fragments.filter(getEq('groupId', groupId))
)

const templateGroupFragmentsSelector = createSelector(
  fragmentsByTemplateIdSelector,
  (_, props) => props.groupId,
  (fragments, groupId) => fragments.filter(getEq('groupId', groupId))
)

export const getTemplateGroupFragments = createToJsSelector(
  templateGroupFragmentsSelector
)

const getValue = getIn(['configuration', 'value'])
const groupBoundValueFragment = createSelector(
  groupFragmentsSelector,
  (_, props) => props.predicate,
  (groupFragments, predicate) => {
    if (groupFragments.isEmpty()) return Map()

    return groupFragments.reduce((maxFragment, currentFragment) => {
      const currentValue = getValue(currentFragment)
      const maxValue = getValue(maxFragment)

      // this will be the first fragment
      if (maxFragment.isEmpty()) return currentFragment

      // always return the maxFragment when currentValue is not a number
      if (!isNumber(currentValue)) return maxFragment

      // check to see if the current value passes the predicate function (e.g. greaterThan)
      if (predicate(currentValue, maxValue)) return currentFragment

      return maxFragment
    }, Map())
  }
)

export const makeGetGroupBoundValueFragment = () =>
  createSelector(groupBoundValueFragment, safeToJS)

const buildGroupedFragmentsWithBounds = (groupFragments) => {
  const relationships = groupFragments.reduce(
    (acc, current) => {
      const enables = current.getIn(['configuration', 'enables'])
      const enabledBy = current.getIn(['configuration', 'enabledBy', 'refNos'])
      const lowerBound = current.getIn(['configuration', 'lowerBound'])
      const upperBound = current.getIn(['configuration', 'upperBound'])

      if (enables) {
        return acc.update('enables', (prevSet) => Set([...prevSet, ...enables]))
      }

      if (enabledBy) {
        const updateEnabledSet = update('enabledBy', (prevSet) =>
          Set([...prevSet, ...enabledBy])
        )
        const setLowerBound = set('lowerBound', lowerBound)
        const setUpperBound = set('upperBound', upperBound)

        const transform =
          lowerBound || upperBound
            ? compose(updateEnabledSet, setLowerBound, setUpperBound)
            : updateEnabledSet

        return transform(acc)
      }

      return acc
    },
    Map({
      enables: Set(),
      enabledBy: Set(),
      lowerBound: List(),
      upperBound: List(),
    })
  )

  return Map({
    fragments: groupFragments,
    enables: relationships.get('enables'),
    enabledBy: relationships.get('enabledBy'),
    lowerBound: relationships.get('lowerBound'),
    upperBound: relationships.get('upperBound'),
  })
}

const groupFragmentsWithBoundsFragments = createSelector(
  groupFragmentsSelector,
  (groupFragments) => buildGroupedFragmentsWithBounds(groupFragments)
)

const groupTemplateFragmentsWithBounds = createSelector(
  templateGroupFragmentsSelector,
  (groupFragments) => buildGroupedFragmentsWithBounds(groupFragments)
)

export const getGroupFragmentsWithBoundsFragments = createToJsSelector(
  groupFragmentsWithBoundsFragments
)

export const getGroupTemplateFragmentsWithBounds = createToJsSelector(
  groupTemplateFragmentsWithBounds
)

const isFragmentEnabled = (fragment, fragments) => {
  const enabledBy = fragment?.getIn(['configuration', 'enabledBy'])

  const conditions = fragment?.getIn([
    'configuration',
    'enabledBy',
    'conditions',
  ])
  // if there is not enabledBy prop then we know it is a stand alone fragment
  if (!enabledBy) {
    return fragment?.getIn(['configuration', 'value'])
  }

  // only one refNo to check
  if (!conditions) {
    const refNo = enabledBy.getIn(['refNos', 0])
    const fragmentValue = fragments.getIn([refNo, 'configuration', 'value'])

    return fragmentValue
  }

  // enabled is based on a condition, eg 'ref1' or 'ref2'
  return conditions.reduce((enabled, condition) => {
    if (enabled) return true

    const ref1 = condition.getIn(['refNos', 0])
    const ref2 = condition.getIn(['refNos', 1])
    const operator = condition.get('operator')

    const ref1FragmentValue = fragments.getIn([ref1, 'configuration', 'value'])
    const ref2FragmentValue = fragments.getIn([ref2, 'configuration', 'value'])

    const evalCondition = (val1, val2, op) => {
      if (op === 'or') {
        return val1 || val2
      }

      if (op === 'and') {
        return val1 && val2
      }

      return val1
    }

    return evalCondition(ref1FragmentValue, ref2FragmentValue, operator)
  }, false)
}

export const makeGetFragmentIsEnabled = () =>
  createSelector(fragmentByIdSelector, fragmentsSelector, isFragmentEnabled)

// gets all eabled fragment ids in a Set
const enabledFragmentIdsSelector = createSelector(
  fragmentsSelector,
  (fragments) =>
    Set(
      fragments
        .filter((fragment) => isFragmentEnabled(fragment, fragments))
        .keySeq()
    )
)

export const getEnabledFragmentIds = createDeepEqualSelector(
  enabledFragmentIdsSelector,
  identity
)

export const boundValues = (bounds, fragments, predicate) => {
  if (!bounds) return Map()

  return bounds.reduce(
    (acc, current) => {
      const fragment = fragments.get(current)
      const configuration = fragment?.get('configuration')
      // If there's no configuration at all, the fragment is not compatible with the vehicle's variant,
      // and is basically out of the picture entirely (unavailable)
      if (!configuration) return acc

      const fragmentValue = fragment?.getIn(['configuration', 'value'])

      return acc
        .update('value', predicate(fragmentValue))
        .update('fragments', push(fragment))
    },
    Map({
      value: null,
      fragments: List(),
    })
  )
}

const groupBoundariesSelector = createSelector(
  fragmentsSelector,
  fragmentsByTemplateIdSelector,
  (_, props) => props,
  (fragments, templateFragments, { lowerBound, upperBound }) => {
    const selectedFragments = templateFragments || fragments
    const upperBounds = boundValues(upperBound, selectedFragments, safeMin)
    const lowerBounds = boundValues(lowerBound, selectedFragments, safeMax)
    return Map({
      upperBoundMin: upperBounds.get('value'),
      lowerBoundMax: lowerBounds.get('value'),
      upperBoundFragments: upperBounds.get('fragments'),
      lowerBoundFragments: lowerBounds.get('fragments'),
    })
  }
)

export const getGroupBoundaries = createToJsSelector(groupBoundariesSelector)

// get the first reference number of the fragment that has definedValues
const firstRefNo = compose(
  get('refNo'),
  find(
    getInSatisfies(
      (definedValues) => definedValues && !definedValues.isEmpty(),
      ['configuration', 'definedValues']
    )
  )
)

// TODO: delete?
export const getDefaultFragmentId = createSelector(
  domainFragmentsSelector,
  firstRefNo
)

const getGroupId = createSelector(fragmentByIdSelector, get('groupId'))

export const makeGetGroupId = () => createSelector(getGroupId, identity)

const getFramgentsForPtoGroup = createSelector(
  fragmentsSelector,
  (_, props) => props.ids,
  (fragments, ids) =>
    ids.reduce((acc, current) => {
      const fragment = fragments.getIn([current])
      return acc.push(fragment)
    }, List())
)

export const getSelectedPtoGroup = createToJsSelector(getFramgentsForPtoGroup)

const ptoSelector = (state) => state.getIn(['pto', 'selectedGroupConfig'])

export const getSelectedGroupConfig = createToJsSelector(ptoSelector)

export const selectSelectedTemplateChanges = createSelector(
  selectedTemplateByIdSelector,
  (template) => template.get('changes')
)

export const getSelectedTemplateChanges = createToJsSelector(
  selectSelectedTemplateChanges
)

export const selectFragmentsOutOfRange = createSelector(
  selectedTemplateByIdSelector,
  (template) =>
    template
      .get('fragments', Map())
      .filter(
        (fragment) =>
          fragment?.get('isFragmentOutOfRange') &&
          fragment?.get('fromDefaultString') !== true
      )
      .valueSeq()
)

export const getFragmentsOutOfRange = createToJsSelector(
  selectFragmentsOutOfRange
)
